import styled from '@emotion/styled'
import PropTypes from 'prop-types'

import { AdviceColumnHeading } from '../advice/shared'
import {
  categoryIcon,
  formatCurrentValue,
  formatRecommendedValue,
  barWidth,
  NO_BALANCE_NOTE,
} from './shared'

// Current values extend left from the center rule; recommended values extend right.
const GRAPH_WIDTH = 402
const GRAPH_COLUMN_WIDTH = GRAPH_WIDTH / 2

// Shared by the value columns so the amount lines and bars stack consistently.
const BAR_HEIGHT = '14px'
const BAR_MARGIN_BOTTOM = '2px'
const AMOUNT_LINE_HEIGHT = '16px'
const AMOUNT_MARGIN_BOTTOM = '6px'

const Row = styled.div`
  display: flex;
  align-items: stretch;
  position: relative;
  margin-top: ${p => (p.groupStart ? '32px' : '0')};
`

const IconCell = styled.div`
  flex: 0 0 64px;
  display: flex;
  align-items: center;
  min-height: 32px;
  padding-left: 16px;
  padding-right: 16px;
  /* Keep these in step with RowContent's top padding and NameCell's bottom
     inset, which share the same values so the icon centers on the same content
     box as the name beside it. */
  padding-top: ${p => (p.header ? '0' : '48px')};
  padding-bottom: ${p => (p.header ? '0' : '10px')};

  svg .icon-color {
    fill: ${p => p.theme.mediumGray};
  }
`

const RowContent = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: stretch;
  position: relative;
  padding-top: ${p => (p.header ? '0' : '48px')};
`

const NameCell = styled.div`
  display: flex;
  align-items: center;
  width: calc(100% - ${GRAPH_WIDTH}px);
  /* Allow the name/legend to shrink below its content width so a wide legend
     wraps instead of pushing the fixed graph columns past the container edge. */
  min-width: 0;
  color: ${p => p.theme.darkestGray};
  font-size: 14px;
  padding-bottom: ${p => (p.spaced ? '10px' : '0')};
`

const GraphColumns = styled.div`
  display: flex;
  align-items: stretch;
  width: ${GRAPH_WIDTH}px;
`

const GraphColumn = styled.div`
  width: ${GRAPH_COLUMN_WIDTH}px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  font-size: 14px;
`

const CenterRule = styled.div`
  flex: 0 0 auto;
  align-self: stretch;
  border-left: 1px solid ${p => (p.hide ? 'transparent' : p.theme.lightestGray)};
`

// Deliberately overflows its own box so the per-row segments join into one
// unbroken vertical line down the table.
const ExtendedCenterRule = styled(CenterRule)`
  margin-bottom: -20px;
`

const AllocationRows = styled.div`
  margin-top: 20px;
`

const CurrentAmount = styled.div`
  text-align: right;
  line-height: ${AMOUNT_LINE_HEIGHT};
  margin: 0 16px ${AMOUNT_MARGIN_BOTTOM} 0;
  color: ${p => p.theme.lightestGray};
`

const RecommendedAmount = styled.div`
  line-height: ${AMOUNT_LINE_HEIGHT};
  margin: 0 0 ${AMOUNT_MARGIN_BOTTOM} 16px;
  color: ${p => p.theme.primaryColor};
`

const ZeroBalanceNote = styled.div`
  font-size: 13px;
  color: ${p => p.theme.darkestGray};
  margin-bottom: 24px;
`

const CurrentBar = styled.div`
  height: ${BAR_HEIGHT};
  margin-left: auto;
  margin-bottom: ${BAR_MARGIN_BOTTOM};
  width: ${p => p.width}%;
  background-color: ${p => p.theme.lightestGray};
`

const RecommendedBar = styled.div`
  height: ${BAR_HEIGHT};
  margin-bottom: ${BAR_MARGIN_BOTTOM};
  width: ${p => p.width}%;
  background-color: ${p => p.theme.primaryColor};
`

const DottedLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  border-bottom: ${p => `1px dotted ${p.theme.primaryBright}`};
`

// The 16px inset off the center rule matches CurrentAmount and RecommendedAmount,
// so each label's inner edge lines up with the values beneath it.
const HeaderLabel = styled(AdviceColumnHeading)`
  text-align: ${p => (p.planned ? 'left' : 'right')};
  margin: ${p => (p.planned ? '0 0 0 16px' : '0 16px 0 0')};
`

// Sits on one row when there's room and wraps (stacking toward a column) as the
// name area narrows, so it never forces the fixed-width graph columns to
// overflow the container.
const Legend = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 8px;
`

const LegendItem = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  column-gap: 6px;
  white-space: nowrap;
  color: ${p => p.theme.lightestGray};
  font-size: 14px;

  & svg {
    width: 20px;
    height: 20px;
  }

  & svg .icon-color {
    fill: ${p => p.theme.mediumGray};
  }
`

const AllocationRow = ({ name, icon, current, recommended, groupStart, zeroBalance }) => (
  <Row groupStart={groupStart}>
    <IconCell>{icon}</IconCell>

    <RowContent>
      <NameCell spaced>{name}</NameCell>

      <GraphColumns>
        <GraphColumn>
          {/* Hide explicit zeroes returned for the current side of zero-balance accounts. */}
          {!zeroBalance && current && (
            <>
              <CurrentAmount>{formatCurrentValue(current)}</CurrentAmount>
              <CurrentBar width={barWidth(current)} />
            </>
          )}
        </GraphColumn>

        <ExtendedCenterRule />

        <GraphColumn>
          {recommended && (
            <>
              <RecommendedAmount>
                {formatRecommendedValue(recommended, zeroBalance)}
              </RecommendedAmount>
              <RecommendedBar width={barWidth(recommended)} />
            </>
          )}
        </GraphColumn>
      </GraphColumns>
    </RowContent>

    <DottedLine />
  </Row>
)

const DesktopAssetAllocation = ({ categories, level, zeroBalance, isModified, className }) => {
  const showInvestments = level === 'investment'

  const allocationRows = showInvestments
    ? categories.flatMap((category, categoryIndex) =>
        (category.investments || []).map((investment, investmentIndex) => (
          <AllocationRow
            key={category.name + investment.name}
            name={investment.name}
            icon={investmentIndex === 0 ? categoryIcon(category.name) : null}
            current={investment.current}
            recommended={investment.recommended}
            groupStart={investmentIndex === 0 && categoryIndex > 0}
            zeroBalance={zeroBalance}
          />
        ))
      )
    : categories.map(category => (
        <AllocationRow
          key={category.name}
          name={category.name}
          icon={categoryIcon(category.name)}
          current={category.current}
          recommended={category.recommended}
          zeroBalance={zeroBalance}
        />
      ))

  return (
    <div className={className}>
      {zeroBalance && <ZeroBalanceNote>{NO_BALANCE_NOTE}</ZeroBalanceNote>}

      <Row>
        <IconCell header />

        <RowContent header>
          <NameCell>
            {showInvestments && (
              <Legend>
                {categories.map(category => (
                  <LegendItem key={category.name}>
                    {categoryIcon(category.name)}
                    {category.name}
                  </LegendItem>
                ))}
              </Legend>
            )}
          </NameCell>

          <GraphColumns>
            <GraphColumn>
              <HeaderLabel>Current</HeaderLabel>
            </GraphColumn>

            <CenterRule hide />

            <GraphColumn>
              <HeaderLabel planned>{isModified ? 'Your changes' : 'Recommended'}</HeaderLabel>
            </GraphColumn>
          </GraphColumns>
        </RowContent>
      </Row>

      <AllocationRows>{allocationRows}</AllocationRows>
    </div>
  )
}

const valueShape = PropTypes.shape({
  percent: PropTypes.number,
  dollar: PropTypes.number,
})

DesktopAssetAllocation.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      current: valueShape,
      recommended: valueShape,
      investments: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          current: valueShape,
          recommended: valueShape,
        })
      ),
    })
  ).isRequired,
  level: PropTypes.oneOf(['overall', 'investment']),
  /** Zero-balance account: percentages only, plus the note and no-balance label. */
  zeroBalance: PropTypes.bool,
  /** Relabels the plan column as "Your changes" when displaying a modified case. */
  isModified: PropTypes.bool,
  className: PropTypes.string,
}

export default DesktopAssetAllocation
